/**
 * OMNICHANNEL-CONVERSATION-SPINE-V1 (Step 3) -- THE lead conversation
 * TRANSCRIPT: the patient's stored WhatsApp / webchat messages for the
 * CURRENT episode, rendered in the record's timeline lane (the lane that
 * used to show the red "The timeline could not be loaded." box for an
 * org-plane lead).
 *
 * WHAT IT SHOWS
 *   - chronological (oldest first, the wire order -- never re-sorted here);
 *   - DIRECTION visible at a glance: an inbound bubble on the left labelled
 *     "Patient", an outbound bubble on the right labelled "Clinic";
 *   - an IST timestamp on every message;
 *   - the deterministic HINTS strip above the thread: a name hint and the
 *     intent tokens, each labelled "from the message, not confirmed" -- an
 *     extracted value is a hint, never verified identity (Step 4 hard
 *     rules); the operator corrects it, this surface never writes it.
 *
 * ZERO STATE (never a red error, never blank): the copy NAMES what will
 * appear and what act produces it. Two honest empties are distinguished --
 *   - store CONNECTED, no messages yet (a lead captured before the store
 *     went live, or a non-message channel): "No WhatsApp messages stored for
 *     this lead yet ... the next message the patient sends ...";
 *   - store NOT LIVE (the FILE-ONLY migration unapplied): "The conversation
 *     store is not live yet ..." -- the honest-seam law.
 *
 * SAFETY
 *   - NO patient identifier in any URL: this component renders no links
 *     and no ids into hrefs (pinned by test);
 *   - message bodies arrive ONLY on the role-gated single-lead timeline
 *     wire; this component fetches nothing (pure render);
 *   - an unreadable body (ciphertext the server could not decrypt) renders
 *     an honest placeholder line, never a fabricated message.
 *
 * TABLET-SAFE: no controls smaller than the 44px floor (the only
 * interactive element is the Reload door on the lane's error state, drawn
 * through the shared QuietAction primitive and its hit-area floor); no
 * pinch-zoom dependency (the thread wraps at every width). The agent case
 * is desktop; both read the same markup.
 *
 * Pure: react-dom/server renderable (the repo's pinned test idiom).
 */

import { formatTimestampIst } from '@/lib/datetime-ist';
import { QuietAction } from '@/dashboard/ui';

import './lead-conversation-transcript.css';

export type LeadConversationDirection = 'inbound' | 'outbound';
export type LeadConversationStreamStatus = 'available' | 'unavailable';

export interface LeadConversationMessageView {
  readonly id: number;
  readonly direction: LeadConversationDirection;
  readonly channel: 'whatsapp' | 'webchat';
  readonly messageType?: string;
  readonly body: string | null;
  readonly bodyStatus?: 'readable' | 'unreadable';
  readonly nameHint?: string | null;
  readonly intents?: readonly string[];
  readonly occurredAt: string;
}

export interface LeadConversationHintsView {
  readonly nameHint: string | null;
  readonly intents: readonly string[];
}

export interface LeadConversationView {
  readonly status: LeadConversationStreamStatus;
  readonly messages: readonly LeadConversationMessageView[];
  readonly truncated?: boolean;
  readonly hints: LeadConversationHintsView;
}

/** The lawful zero states -- each names WHAT will appear and WHAT act
 *  produces it (never "no data", never a red box). */
export const CONVERSATION_EMPTY_CONNECTED_TITLE =
  'No WhatsApp messages stored for this lead yet.';
export const CONVERSATION_EMPTY_CONNECTED_BODY =
  'The next message the patient sends on WhatsApp appears here, with the reply the clinic sent back.';
export const CONVERSATION_SEAM_TITLE = 'The conversation store is not live yet.';
export const CONVERSATION_SEAM_BODY =
  'Once the founder applies the conversation store, every new WhatsApp message on this lead appears here in order.';
export const CONVERSATION_UNREADABLE_BODY_COPY =
  'Message stored but could not be read on this environment.';
export const CONVERSATION_TRUNCATED_COPY =
  'Showing the most recent part of a long conversation.';
export const CONVERSATION_HINT_LABEL = 'From the messages (not confirmed)';
export const CONVERSATION_TITLE = 'Conversation';
export const CONVERSATION_LOADING_COPY = 'Loading the conversation...';
export const CONVERSATION_ERROR_TITLE = 'The conversation could not be loaded right now.';
export const CONVERSATION_ERROR_BODY =
  'The record still works. Reload to try again; if it keeps failing, tell your manager.';

/** The operator-facing intent vocabulary labels (mirrors the ONE code
 *  vocabulary in lib/conversation-extraction.ts by token; an unknown token
 *  renders as itself, never dropped). */
export const CONVERSATION_INTENT_LABEL: Readonly<Record<string, string>> = {
  appointment: 'Wants an appointment',
  treatment: 'Asked about a treatment',
  price: 'Asked about price',
  location: 'Asked about location',
  emergency: 'In pain or an emergency',
};

export function intentLabel(token: string): string {
  return CONVERSATION_INTENT_LABEL[token] ?? token;
}

export const DIRECTION_LABEL: Readonly<Record<LeadConversationDirection, string>> = {
  inbound: 'Patient',
  outbound: 'Clinic',
};

/**
 * Read the conversation leg off the timeline wire WITHOUT widening the
 * shared api.ts response type (the endpoint's JSON carries `conversation`;
 * an older server or a malformed proxy answer yields null and the lane
 * renders the honest seam). Pure; total over unknown input.
 */
export function readConversationFromTimeline(data: unknown): LeadConversationView | null {
  if (data === null || typeof data !== 'object') return null;
  const raw = (data as { conversation?: unknown }).conversation;
  if (raw === null || typeof raw !== 'object') return null;
  const c = raw as {
    status?: unknown;
    messages?: unknown;
    truncated?: unknown;
    hints?: unknown;
  };
  const status: LeadConversationStreamStatus =
    c.status === 'available' ? 'available' : 'unavailable';
  const messages: LeadConversationMessageView[] = Array.isArray(c.messages)
    ? c.messages
        .filter((m): m is Record<string, unknown> => m !== null && typeof m === 'object')
        .map((m) => ({
          id: typeof m.id === 'number' ? m.id : 0,
          direction: m.direction === 'outbound' ? 'outbound' : 'inbound',
          channel: m.channel === 'webchat' ? 'webchat' : 'whatsapp',
          ...(typeof m.messageType === 'string' ? { messageType: m.messageType } : {}),
          body: typeof m.body === 'string' ? m.body : null,
          bodyStatus: typeof m.body === 'string' ? 'readable' : 'unreadable',
          nameHint: typeof m.nameHint === 'string' ? m.nameHint : null,
          intents: Array.isArray(m.intents)
            ? m.intents.filter((t): t is string => typeof t === 'string')
            : [],
          occurredAt: typeof m.occurredAt === 'string' ? m.occurredAt : '',
        }))
    : [];
  const hintsRaw =
    c.hints !== null && typeof c.hints === 'object'
      ? (c.hints as { nameHint?: unknown; intents?: unknown })
      : {};
  return {
    status,
    messages,
    truncated: c.truncated === true,
    hints: {
      nameHint: typeof hintsRaw.nameHint === 'string' ? hintsRaw.nameHint : null,
      intents: Array.isArray(hintsRaw.intents)
        ? hintsRaw.intents.filter((t): t is string => typeof t === 'string')
        : [],
    },
  };
}

function HintsStrip({ hints }: { hints: LeadConversationHintsView }): JSX.Element | null {
  const hasName = typeof hints.nameHint === 'string' && hints.nameHint.trim() !== '';
  if (!hasName && hints.intents.length === 0) return null;
  return (
    <div className="lead-convo__hints" data-testid="lead-conversation-hints">
      <span className="lead-convo__hints-label">{CONVERSATION_HINT_LABEL}:</span>
      {hasName ? (
        <span className="lead-convo__hint lead-convo__hint--name" data-testid="lead-conversation-hint-name">
          Name: {hints.nameHint}
        </span>
      ) : null}
      {hints.intents.map((t) => (
        <span
          key={t}
          className="lead-convo__hint lead-convo__hint--intent"
          data-testid="lead-conversation-hint-intent"
          data-intent={t}
        >
          {intentLabel(t)}
        </span>
      ))}
    </div>
  );
}

function MessageRow({ message }: { message: LeadConversationMessageView }): JSX.Element {
  const direction = message.direction;
  const readable = message.body !== null && message.bodyStatus !== 'unreadable';
  return (
    <li
      className={`lead-convo__row lead-convo__row--${direction}`}
      data-testid="lead-conversation-message"
      data-direction={direction}
    >
      <div className={`lead-convo__bubble lead-convo__bubble--${direction}`}>
        <div className="lead-convo__meta">
          <span className="lead-convo__who">{DIRECTION_LABEL[direction]}</span>
          <span className="lead-convo__when" data-testid="lead-conversation-when">
            {formatTimestampIst(message.occurredAt)}
          </span>
        </div>
        {readable ? (
          <p className="lead-convo__body" data-testid="lead-conversation-body">
            {message.body}
          </p>
        ) : (
          <p
            className="lead-convo__body lead-convo__body--unreadable"
            data-testid="lead-conversation-body-unreadable"
          >
            {CONVERSATION_UNREADABLE_BODY_COPY}
          </p>
        )}
      </div>
    </li>
  );
}

export interface LeadConversationTranscriptProps {
  readonly conversation: LeadConversationView | null;
  /** The timeline wire state the lane rides (loading / error / ready). */
  readonly status?: 'loading' | 'error' | 'ready';
  readonly onRetry?: () => void;
  readonly testId?: string;
}

/**
 * The transcript block. Renders entirely from props (no fetch, no hooks
 * beyond none). The lane's own loading / error states are honest and
 * calm; the empties name the act that fills them.
 */
export default function LeadConversationTranscript({
  conversation,
  status = 'ready',
  onRetry,
  testId = 'lead-conversation',
}: LeadConversationTranscriptProps): JSX.Element {
  if (status === 'loading') {
    return (
      <section className="lead-convo" aria-label="Lead conversation" data-testid={testId}>
        <h3 className="lead-convo__title">{CONVERSATION_TITLE}</h3>
        <p className="lead360-muted" data-testid={`${testId}-loading`}>
          {CONVERSATION_LOADING_COPY}
        </p>
      </section>
    );
  }
  if (status === 'error') {
    return (
      <section className="lead-convo" aria-label="Lead conversation" data-testid={testId}>
        <h3 className="lead-convo__title">{CONVERSATION_TITLE}</h3>
        <div className="ac-empty lead-convo__empty" data-testid={`${testId}-error`}>
          <p className="ac-empty__title">{CONVERSATION_ERROR_TITLE}</p>
          <p className="ac-empty__body">{CONVERSATION_ERROR_BODY}</p>
          {onRetry !== undefined ? (
            <QuietAction
              type="button"
              size="sm"
              className="lead-convo__retry"
              onClick={onRetry}
              data-testid={`${testId}-retry`}
            >
              Reload
            </QuietAction>
          ) : null}
        </div>
      </section>
    );
  }

  const view: LeadConversationView =
    conversation ?? { status: 'unavailable', messages: [], hints: { nameHint: null, intents: [] } };

  if (view.messages.length === 0) {
    const seam = view.status !== 'available';
    return (
      <section className="lead-convo" aria-label="Lead conversation" data-testid={testId}>
        <h3 className="lead-convo__title">{CONVERSATION_TITLE}</h3>
        <div
          className="ac-empty lead-convo__empty"
          data-testid={seam ? `${testId}-empty-seam` : `${testId}-empty-connected`}
          data-store-status={view.status}
        >
          <p className="ac-empty__title">
            {seam ? CONVERSATION_SEAM_TITLE : CONVERSATION_EMPTY_CONNECTED_TITLE}
          </p>
          <p className="ac-empty__body">
            {seam ? CONVERSATION_SEAM_BODY : CONVERSATION_EMPTY_CONNECTED_BODY}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="lead-convo" aria-label="Lead conversation" data-testid={testId}>
      <div className="lead-convo__head">
        <h3 className="lead-convo__title">{CONVERSATION_TITLE}</h3>
        <span className="lead360-muted lead-convo__count" data-testid={`${testId}-count`}>
          {view.messages.length} {view.messages.length === 1 ? 'message' : 'messages'}
        </span>
      </div>
      <HintsStrip hints={view.hints} />
      {view.truncated === true ? (
        <p className="lead360-muted" data-testid={`${testId}-truncated`}>
          {CONVERSATION_TRUNCATED_COPY}
        </p>
      ) : null}
      <ol className="lead-convo__thread" data-testid={`${testId}-thread`}>
        {view.messages.map((m) => (
          <MessageRow key={m.id} message={m} />
        ))}
      </ol>
    </section>
  );
}
